import React from 'react';
import InputField from 'components/pure/utils/form/InputField';
import debounce from 'lodash/debounce';
import './AsyncAutocomplete.styl';
import Loading from 'components/pure/utils/Loading';
import ClearIcon from 'material-ui/svg-icons/content/remove-circle';
import theme from 'styles/theme';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import MultiLineTextOverflow from 'components/pure/utils/MultiLineTextOverflow';

const debounceTime = 300;

const emptyState = {
  isLoading: false,
  searchText: undefined,
  autocompleteOptions: undefined,
  selectedValue: undefined,
};

/**
 * Generic autocomplete (including prefix search against the server)
 */
export default class AsyncAutocomplete extends React.Component {
  static propTypes = {
    // placeholder for the input field
    placeholder: PropTypes.string,
    // how wide should the autocomplete be?
    width: PropTypes.number,
    // pass the function ((term, limit?) => Promise) to fetch results
    doSearch: PropTypes.func,
    // pass the function (({id, freeformText}) => void) to callback when selected
    // either the id or text will be filled, text only if allowFreeform
    // also invoked when the user clears the form (both undefined in this case)
    onSelect: PropTypes.func,
    // how to fetch the id from the result, default r.id
    idFromResult: PropTypes.func,
    // how to fetch the display value from the result, default r.display
    displayFromResult: PropTypes.func,
    // should we let the user type & select whatever they want, or are they
    // bound to only the typeahead values?
    allowFreeform: PropTypes.bool,
  };

  constructor(props) {
    super(props);

    this.state = emptyState;

    this.doSearch = this.doSearch.bind(this);
    this.onSelect = this.onSelect.bind(this);
    this.onSelectFreeform = this.onSelectFreeform.bind(this);
    this.onDocumentClick = this.onDocumentClick.bind(this);
  }

  componentDidMount() {
    const {doSearch} = this.props;

    this.debouncedSearch = debounce(
      (term, callback) => doSearch(term).then(callback),
      debounceTime
    );
  }

  doSearch(val) {
    this.setState({autocompleteOptions: undefined, searchText: val});

    if (val == null || val === '' || val.length < 3) return;

    this.setState({
      isLoading: true,
    });

    this.debouncedSearch(val, result =>
      this.setState({autocompleteOptions: result, isLoading: false})
    );

    document.addEventListener('click', this.onDocumentClick);
  }

  onSelect(selectedValue) {
    this.setState({
      selectedValue,
    });
    this.props.onSelect({id: selectedValue});
    document.removeEventListener('click', this.onDocumentClick);
  }

  onSelectFreeform() {
    const {searchText} = this.state;

    this.setState({
      selectedValue: {display: searchText},
    });
    this.props.onSelect({freeformText: searchText});
  }

  onDocumentClick(e) {
    this.dropdownRef &&
      !ReactDOM.findDOMNode(this.dropdownRef).contains(e.target) &&
      this.setState(emptyState);
  }

  componentWillUnmount() {
    document.removeEventListener('click', this.onDocumentClick);
  }

  render() {
    const {
      placeholder,
      width,
      allowFreeform,
      onSelect,
      idFromResult = r => r.id,
      displayFromResult = r => r.display,
    } = this.props;
    const {searchText, isLoading, autocompleteOptions, selectedValue} = this.state;

    return (
      <section className="autocomplete-container" style={{width}}>
        {selectedValue ? (
          <div className="selected-value">
            <MultiLineTextOverflow content={displayFromResult(selectedValue)} />
            <ClearIcon
              color={theme.palette.pastelBad}
              onClick={() => {
                this.setState(emptyState);
                onSelect({id: undefined, freeformText: undefined});
              }}
            />
          </div>
        ) : (
          <div>
            <InputField
              value={searchText || ''}
              placeholder={placeholder}
              onChange={this.doSearch}
            />
            {autocompleteOptions || isLoading ? (
              <div className="results" style={{width: width - 2}} ref={r => (this.dropdownRef = r)}>
                {isLoading ? (
                  <Loading />
                ) : (
                  <div>
                    {!autocompleteOptions.isEmpty() ? (
                      autocompleteOptions.map(o => (
                        <div
                          className="result"
                          key={idFromResult(o)}
                          onClick={() => this.onSelect(o)}>
                          {displayFromResult(o)}
                        </div>
                      ))
                    ) : allowFreeform ? (
                      <div>
                        <div className="result" onClick={this.onSelectFreeform}>
                          <strong>New value:</strong> {searchText}
                        </div>
                      </div>
                    ) : (
                      <div className="result empty">No results!</div>
                    )}
                  </div>
                )}
              </div>
            ) : null}
          </div>
        )}
      </section>
    );
  }
}
